import { useEffect, useState } from "react";
import { apiClient } from "../App";
import { Button } from "./ui/button";
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from "./ui/sheet";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Layers, ChevronLeft, ChevronRight, Download, Users } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/**
 * Pay Group Summary — owed-vs-paid matrix by month and pay group.
 *
 * Default view: rows = pay groups, columns = months, each cell shows
 *  Owed (top, blue) / Paid (bottom, teal). Cells are clickable to drill into
 * the per-employee breakdown for that (group, month).
 *
 * Owners can also flip the table to "Balance only" to see what's still due.
 */
const PayGroupSummarySheet = ({ open, onOpenChange }) => {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [view, setView] = useState("owed_paid"); // owed_paid | balance | gross | paid
  const [bucketBy, setBucketBy] = useState("payment_date"); // payment_date | pay_period
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drill, setDrill] = useState(null); // { pay_group_id, month, group_name }
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const fetchSummary = async (y, b) => {
    setLoading(true);
    setData(null);
    try {
      const res = await apiClient.get(`/payments/summary?year=${y}&bucket_by=${b}`);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load summary");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchSummary(year, bucketBy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, year, bucketBy]);

  const handleDrill = async (row, month) => {
    setDrill({ pay_group_id: row.pay_group_id, group_name: row.group_name, month });
    setDrillLoading(true);
    setDrillData(null);
    try {
      const res = await apiClient.get(
        `/payments/summary/detail?pay_group_id=${encodeURIComponent(row.pay_group_id)}&month=${month}&year=${year}&bucket_by=${bucketBy}`
      );
      setDrillData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load drill-down");
    } finally {
      setDrillLoading(false);
    }
  };

  const exportCsv = () => {
    if (!data) return;
    const headers = ["pay_group", "metric", ...MONTH_LABELS, "Year Total"];
    const lines = [`# bucket_by=${bucketBy}, year=${year}`, headers.join(",")];
    const metrics = view === "balance" ? ["balance"]
      : view === "gross" ? ["gross"]
      : view === "paid" ? ["paid"]
      : ["owed", "paid", "balance"];
    for (const row of data.rows) {
      for (const m of metrics) {
        const cells = row.monthly.map((c) => c[m] || 0);
        const total = row.year_total[m] || 0;
        lines.push([`"${row.group_name}"`, m, ...cells.map((v) => v.toFixed(2)), total.toFixed(2)].join(","));
      }
    }
    for (const m of metrics) {
      const cells = data.monthly_totals.map((c) => (c[m] || 0).toFixed(2));
      lines.push([`"GRAND TOTAL"`, m, ...cells, (data.grand_total[m] || 0).toFixed(2)].join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pay-group-summary-${year}-${bucketBy}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-[95vw] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-[#0D9488]" />
              Pay Group Summary
            </SheetTitle>
            <SheetDescription>
              How much each pay group earned vs how much you&apos;ve paid them, month by month. Click any cell to drill into the people.
            </SheetDescription>
          </SheetHeader>

          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-3 mt-6">
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setYear((y) => y - 1)}
                data-testid="summary-prev-year"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="font-semibold text-[#0A192F] min-w-[60px] text-center" data-testid="summary-year-label">{year}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setYear((y) => y + 1)}
                disabled={year >= currentYear}
                data-testid="summary-next-year"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            <Select value={view} onValueChange={setView}>
              <SelectTrigger className="w-[200px]" data-testid="summary-view-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="owed_paid">Owed & Paid</SelectItem>
                <SelectItem value="balance">Balance only</SelectItem>
                <SelectItem value="gross">Gross earnings only</SelectItem>
                <SelectItem value="paid">Paid only</SelectItem>
              </SelectContent>
            </Select>

            <Select value={bucketBy} onValueChange={setBucketBy}>
              <SelectTrigger className="w-[230px]" data-testid="summary-bucket-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="payment_date">
                  <div className="flex flex-col">
                    <span>Group by Payment date</span>
                    <span className="text-[10px] text-slate-500">When the money went out</span>
                  </div>
                </SelectItem>
                <SelectItem value="pay_period">
                  <div className="flex flex-col">
                    <span>Group by Pay period</span>
                    <span className="text-[10px] text-slate-500">Which period it was for</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              onClick={exportCsv}
              disabled={!data}
              className="ml-auto"
              data-testid="summary-export-csv"
            >
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
          </div>

          {/* Matrix */}
          <div className="mt-6">
            {loading && <div className="py-16 text-center text-slate-500">Computing summary…</div>}
            {data && data.rows.length === 0 && !loading && (
              <div className="py-16 text-center text-slate-500">
                No payroll activity in {year}.
              </div>
            )}
            {data && data.rows.length > 0 && (
              <>
                <div className={`mb-3 px-3 py-2 rounded-md text-xs flex items-center gap-2 ${
                  bucketBy === "pay_period"
                    ? "bg-purple-50 border border-purple-200 text-purple-800"
                    : "bg-slate-50 border border-slate-200 text-slate-600"
                }`} data-testid="summary-bucket-banner">
                  <Layers className="w-3.5 h-3.5 shrink-0" />
                  {bucketBy === "pay_period" ? (
                    <span><strong>Pay-period view:</strong> a $500 payment made June 2 for the May 16-31 period appears under <strong>May</strong>. Use this to confirm "did I clear what I owed for May?".</span>
                  ) : (
                    <span><strong>Payment-date view:</strong> payments bucket by when the money went out. Use this to see "how much went out the door in June?".</span>
                  )}
                </div>
                <div className="border border-slate-200 rounded-md overflow-x-auto" data-testid="summary-matrix">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs uppercase sticky left-0 bg-slate-50 z-10 min-w-[180px]">Pay Group</TableHead>
                        {MONTH_LABELS.map((m, i) => (
                          <TableHead key={m} className="text-xs uppercase text-right min-w-[100px]">
                            {m}
                            <div className="text-[10px] text-slate-400 font-normal">{year}-{String(i + 1).padStart(2, "0")}</div>
                          </TableHead>
                        ))}
                        <TableHead className="text-xs uppercase text-right bg-slate-100 min-w-[110px]">Year Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.rows.map((row) => (
                        <TableRow key={row.pay_group_id} data-testid={`summary-row-${row.pay_group_id}`}>
                          <TableCell className="font-medium sticky left-0 bg-white z-10 border-r border-slate-100">
                            <div>{row.group_name}</div>
                            <div className="text-[11px] text-slate-400">{row.employee_count} employee{row.employee_count === 1 ? "" : "s"}</div>
                          </TableCell>
                          {row.monthly.map((cell) => (
                            <TableCell
                              key={cell.month}
                              className="text-right cursor-pointer hover:bg-slate-50 transition-colors p-2"
                              onClick={() => (cell.owed || cell.paid) ? handleDrill(row, cell.month) : null}
                              data-testid={`summary-cell-${row.pay_group_id}-${cell.month}`}
                            >
                              <SummaryCell cell={cell} view={view} />
                            </TableCell>
                          ))}
                          <TableCell className="text-right bg-slate-50 p-2">
                            <SummaryCell cell={row.year_total} view={view} bold />
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Grand total row */}
                      <TableRow className="bg-slate-100 font-semibold" data-testid="summary-grand-total-row">
                        <TableCell className="sticky left-0 bg-slate-100 z-10 border-r border-slate-200">
                          Grand Total
                        </TableCell>
                        {data.monthly_totals.map((cell) => (
                          <TableCell key={cell.month} className="text-right p-2">
                            <SummaryCell cell={cell} view={view} bold />
                          </TableCell>
                        ))}
                        <TableCell className="text-right bg-slate-200 p-2">
                          <SummaryCell cell={data.grand_total} view={view} bold />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
                <p className="text-xs text-slate-500 mt-3">
                  <span className="font-semibold">Owed</span> = Gross earnings + Extras − Deductions.{" "}
                  <span className="font-semibold">Balance</span> = Owed − Paid (red = still owed, green = overpaid).
                </p>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Drill-down dialog */}
      <Dialog open={!!drill} onOpenChange={(o) => { if (!o) { setDrill(null); setDrillData(null); } }}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#0D9488]" />
              {drill?.group_name} · {drill?.month}
            </DialogTitle>
            <DialogDescription>Who contributed to this cell — earnings, deductions, and payments.</DialogDescription>
          </DialogHeader>
          {drillLoading && <div className="py-12 text-center text-slate-500">Loading…</div>}
          {drillData && (
            <div className="space-y-3" data-testid="summary-drill-body">
              <div className="grid grid-cols-4 gap-3 text-sm">
                <Tile label="Owed" value={drillData.total.owed} color="text-[#0A192F]" />
                <Tile label="Paid" value={drillData.total.paid} color="text-[#0D9488]" prefix="−" />
                <Tile label="Deductions" value={drillData.total.deductions} color="text-[#E11D48]" prefix="−" />
                <Tile label="Balance" value={drillData.total.balance} color={drillData.total.balance > 0 ? "text-[#E11D48]" : "text-[#10B981]"} />
              </div>
              <div className="border border-slate-200 rounded-md overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs uppercase">Employee</TableHead>
                      <TableHead className="text-xs uppercase">Type</TableHead>
                      <TableHead className="text-xs uppercase text-right">Gross</TableHead>
                      <TableHead className="text-xs uppercase text-right">Extras</TableHead>
                      <TableHead className="text-xs uppercase text-right">Deductions</TableHead>
                      <TableHead className="text-xs uppercase text-right">Owed</TableHead>
                      <TableHead className="text-xs uppercase text-right">Paid</TableHead>
                      <TableHead className="text-xs uppercase text-right">Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {drillData.employees.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-slate-500 py-8" data-testid="summary-drill-empty">No employees with activity this month.</TableCell>
                      </TableRow>
                    ) : drillData.employees.map((emp) => (
                      <TableRow key={emp.employee_id} data-testid={`summary-drill-row-${emp.employee_id}`}>
                        <TableCell className="font-medium">
                          {emp.name}
                          {emp.status !== "active" && <span className="ml-2 text-[10px] uppercase text-slate-400">({emp.status})</span>}
                        </TableCell>
                        <TableCell className="capitalize text-xs">{emp.pay_type}</TableCell>
                        <TableCell className="text-right">{fmt(emp.gross)}</TableCell>
                        <TableCell className="text-right text-[#10B981]">{emp.extras > 0 ? `+${fmt(emp.extras)}` : "—"}</TableCell>
                        <TableCell className="text-right text-[#E11D48]">{emp.deductions > 0 ? `−${fmt(emp.deductions)}` : "—"}</TableCell>
                        <TableCell className="text-right font-semibold">{fmt(emp.owed)}</TableCell>
                        <TableCell className="text-right text-[#0D9488]">{emp.paid > 0 ? `−${fmt(emp.paid)}` : "—"}</TableCell>
                        <TableCell className={`text-right font-semibold ${emp.balance > 0 ? "text-[#E11D48]" : emp.balance < 0 ? "text-[#10B981]" : "text-slate-500"}`}>
                          {fmt(emp.balance)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {/* Payment line items */}
              {drillData.employees.some((e) => e.payments.length > 0) && (
                <details className="border border-slate-200 rounded-md">
                  <summary className="bg-slate-50 px-3 py-2 font-semibold cursor-pointer text-sm">Payment line items</summary>
                  <div className="max-h-60 overflow-y-auto divide-y divide-slate-100 text-xs">
                    {drillData.employees.flatMap((emp) =>
                      emp.payments.map((p) => (
                        <div key={p.payment_id} className="px-3 py-2 flex justify-between gap-2">
                          <span>
                            <span className="font-medium">{emp.name}</span>{" "}
                            <span className="text-slate-500">· {p.payment_date} · {p.method}</span>
                            {p.period_start && (
                              <span className="text-slate-400 ml-2">({p.period_start} → {p.period_end})</span>
                            )}
                          </span>
                          <span className="font-medium">{fmt(p.amount)}</span>
                        </div>
                      ))
                    )}
                  </div>
                </details>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDrill(null); setDrillData(null); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const SummaryCell = ({ cell, view, bold = false }) => {
  if (!cell || (cell.owed === 0 && cell.paid === 0 && cell.balance === 0)) {
    return <span className="text-slate-300">—</span>;
  }
  if (view === "balance") {
    const v = cell.balance;
    const color = v > 0 ? "text-[#E11D48]" : v < 0 ? "text-[#10B981]" : "text-slate-500";
    return <span className={`${bold ? "font-semibold" : ""} ${color}`}>{fmt(v)}</span>;
  }
  if (view === "gross") {
    return <span className={bold ? "font-semibold" : ""}>{fmt(cell.gross)}</span>;
  }
  if (view === "paid") {
    return (
      <span className={`text-[#0D9488] ${bold ? "font-semibold" : ""}`} data-testid="cell-paid-only">
        {fmt(cell.paid)}
      </span>
    );
  }
  return (
    <div className="leading-tight">
      <div className={`text-[#0A192F] ${bold ? "font-semibold" : ""}`} data-testid="cell-owed">{fmt(cell.owed)}</div>
      <div className="text-[11px] text-[#0D9488]" data-testid="cell-paid">−{fmt(cell.paid)}</div>
      {Math.abs(cell.balance) > 0.01 && (
        <div className={`text-[11px] ${cell.balance > 0 ? "text-[#E11D48]" : "text-[#10B981]"}`}>
          = {fmt(cell.balance)}
        </div>
      )}
    </div>
  );
};

const Tile = ({ label, value, color, prefix = "" }) => (
  <div className="p-3 bg-slate-50 rounded-md">
    <div className="text-xs text-slate-500 uppercase">{label}</div>
    <div className={`text-lg font-bold ${color}`}>{prefix}{fmt(value)}</div>
  </div>
);

export default PayGroupSummarySheet;
